/**
 * Immutable state-editing helpers shared by every command definition.
 *
 * Two properties every helper here preserves:
 *
 * - **Structural sharing.** Editing one hop rebuilds that hop, that hop array,
 *   that recipe and the `recipes` record — and shares every other object by
 *   reference. A workspace with 200 recipes costs four allocations per
 *   keystroke, which is what makes "recompute on every keystroke" (PLAN §4.2)
 *   affordable.
 * - **Determinism.** No clocks, no randomness, no iteration over unordered
 *   sets. Given the same state and the same command, these produce the same
 *   object graph every time.
 */

import type { BatchDoc } from '../document/batch';
import type { BlendDoc } from '../document/blend';
import type { JournalDoc } from '../document/journal';
import type { RecipeDoc } from '../document/recipe';
import type { WorkspaceState } from '../document/workspace';
import type { ApplyResult, CommandError } from './envelope';
import { commandError } from './envelope';
import type { Command } from './types';

/** True when a `mutate` callback returned a failure rather than a document. */
export function isCommandError(value: object): value is CommandError {
  return 'code' in value && 'message' in value && 'issues' in value;
}

/**
 * A change set: any subset of `T`'s keys, each optionally `undefined`.
 *
 * This is what a Zod `.partial()` infers under `exactOptionalPropertyTypes`,
 * and it is deliberately looser than `Partial<T>`: a payload that has been
 * through JSON or through a client that spreads defaults really can arrive
 * carrying `{ note: undefined }`.
 */
export type Changes<T> = { [P in keyof T]?: T[P] | undefined };

/**
 * Merge a change set onto a record, ignoring keys that are absent or
 * explicitly `undefined`.
 *
 * `{ ...item, ...changes }` cannot be used directly: an explicit `undefined`
 * would blow a required `note: string | null` away.
 */
export function mergeDefined<T extends object>(base: T, changes: Changes<T>): T {
  const next = { ...base };
  for (const key of Object.keys(changes) as (keyof T)[]) {
    const value = changes[key];
    if (value !== undefined) next[key] = value as T[keyof T];
  }
  return next;
}

/**
 * Merge a change set onto a record, writing every key that is **present**,
 * including the ones whose value is `null`.
 *
 * The counterpart to {@link mergeDefined}, for the one family of fields where
 * `null` is a value the user can choose rather than "no value": a batch
 * measurement. Clearing a mistyped pre-boil gravity has to be expressible, and
 * `{ preBoil: { sg: null } }` is how. `undefined` is still skipped — it is what
 * an absent key becomes after a JSON round-trip and back through Zod, so
 * treating it as "clear" would make a re-delivered command destructive.
 */
export function mergePresent<T extends object>(base: T, changes: Changes<T>): T {
  const next = { ...base };
  for (const key of Object.keys(changes) as (keyof T)[]) {
    if (!Object.hasOwn(changes, key)) continue;
    const value = changes[key];
    if (value === undefined) continue;
    next[key] = value as T[keyof T];
  }
  return next;
}

/**
 * The prior values of exactly the keys a change set touches, `null`s included.
 *
 * The inverse-building counterpart to {@link mergePresent}, exactly as
 * {@link priorValues} is to {@link mergeDefined}. They differ only in which
 * keys count as touched, and both must agree with their merge function or undo
 * stops being an identity.
 */
export function priorPresentValues<T extends object>(base: T, changes: Changes<T>): Partial<T> {
  const prior: Partial<T> = {};
  for (const key of Object.keys(changes) as (keyof T)[]) {
    if (changes[key] === undefined) continue;
    prior[key] = base[key];
  }
  return prior;
}

/**
 * The prior values of exactly the keys a change set touches.
 *
 * This is what makes `update` commands invertible: the inverse is the same
 * command with the *old* values of the *same* fields, which is both minimal on
 * the wire and correct under field-granularity last-write-wins (PLAN §8.3).
 */
export function priorValues<T extends object>(base: T, changes: Changes<T>): Partial<T> {
  const prior: Partial<T> = {};
  for (const key of Object.keys(changes) as (keyof T)[]) {
    if (changes[key] !== undefined) prior[key] = base[key];
  }
  return prior;
}

// ---------------------------------------------------------------------------
// List helpers
// ---------------------------------------------------------------------------

/** Index of the item whose `key` field equals `id`, or `-1`. */
export function indexOfId<T, K extends keyof T>(list: readonly T[], key: K, id: string): number {
  return list.findIndex((item) => item[key] === id);
}

/** A copy of `list` with `item` inserted at `index` (clamped), or appended when `index` is absent. */
export function insertAt<T>(list: readonly T[], item: T, index: number | undefined): T[] {
  const next = [...list];
  const at = index === undefined ? next.length : Math.min(Math.max(index, 0), next.length);
  next.splice(at, 0, item);
  return next;
}

/** A copy of `list` with position `index` removed. */
export function removeAt<T>(list: readonly T[], index: number): T[] {
  const next = [...list];
  next.splice(index, 1);
  return next;
}

/** A copy of `list` with position `index` replaced. */
export function replaceAt<T>(list: readonly T[], index: number, item: T): T[] {
  const next = [...list];
  next[index] = item;
  return next;
}

/**
 * Reorder `list` into the order given by `ids`.
 *
 * Strict on purpose: `ids` must be exactly the list's current ids, each once.
 * A partial or padded order would make the operation ambiguous and its inverse
 * lossy, so it is rejected rather than interpreted.
 *
 * @returns the reordered list, or `null` when `ids` is not a permutation.
 */
export function reorderById<T, K extends keyof T>(
  list: readonly T[],
  key: K,
  ids: readonly string[],
): T[] | null {
  if (ids.length !== list.length) return null;
  const byId = new Map<string, T>();
  for (const item of list) byId.set(String(item[key]), item);
  const next: T[] = [];
  for (const id of ids) {
    const item = byId.get(id);
    if (item === undefined) return null;
    byId.delete(id);
    next.push(item);
  }
  return next;
}

/** The list's ids in current order — the payload a `reorder` inverse carries. */
export function idsOf<T, K extends keyof T>(list: readonly T[], key: K): string[] {
  return list.map((item) => String(item[key]));
}

// ---------------------------------------------------------------------------
// Workspace helpers
// ---------------------------------------------------------------------------

/** A copy of `state` with one recipe replaced (or added). */
export function putRecipe(state: WorkspaceState, recipe: RecipeDoc): WorkspaceState {
  return { ...state, recipes: { ...state.recipes, [recipe.id]: recipe } };
}

/** A copy of `state` with one recipe removed. */
export function dropRecipe(state: WorkspaceState, recipeId: string): WorkspaceState {
  const recipes = { ...state.recipes };
  delete recipes[recipeId];
  return { ...state, recipes };
}

/**
 * Look a recipe up, run `mutate` on it, and stamp `updatedAt` from the command.
 *
 * Every recipe-scoped command goes through here, which is why `updatedAt` is
 * uniform and why `invert` can restore it by issuing the inverse with the
 * pre-state's `updatedAt` as its envelope timestamp (see `invert.ts`).
 *
 * @param mutate - Returns the next document, or a {@link CommandError}. It must
 *   not mutate its argument.
 */
export function withRecipe(
  state: WorkspaceState,
  command: Command,
  recipeId: string,
  mutate: (recipe: RecipeDoc) => RecipeDoc | CommandError,
): ApplyResult {
  const recipe = state.recipes[recipeId];
  if (recipe === undefined) {
    return {
      ok: false,
      error: commandError('not-found', `No recipe with id "${recipeId}".`, {
        commandId: command.commandId,
        commandType: command.type,
      }),
    };
  }
  const result = mutate(recipe);
  if (isCommandError(result)) {
    return {
      ok: false,
      error: { ...result, commandId: command.commandId, commandType: command.type },
    };
  }
  return { ok: true, state: putRecipe(state, { ...result, updatedAt: command.timestamp }) };
}

/** A copy of `state` with one batch replaced (or added). */
export function putBatch(state: WorkspaceState, batch: BatchDoc): WorkspaceState {
  return { ...state, batches: { ...state.batches, [batch.id]: batch } };
}

/** A copy of `state` with one batch removed. */
export function dropBatch(state: WorkspaceState, batchId: string): WorkspaceState {
  const batches = { ...state.batches };
  delete batches[batchId];
  return { ...state, batches };
}

/** A copy of `state` with one blend replaced (or added). */
export function putBlend(state: WorkspaceState, blend: BlendDoc): WorkspaceState {
  return { ...state, blends: { ...state.blends, [blend.id]: blend } };
}

/** A copy of `state` with one blend removed. */
export function dropBlend(state: WorkspaceState, blendId: string): WorkspaceState {
  const blends = { ...state.blends };
  delete blends[blendId];
  return { ...state, blends };
}

/** A copy of `state` with one journal replaced (or added). */
export function putJournal(state: WorkspaceState, journal: JournalDoc): WorkspaceState {
  return { ...state, journals: { ...state.journals, [journal.id]: journal } };
}

/** A copy of `state` with one journal removed. */
export function dropJournal(state: WorkspaceState, journalId: string): WorkspaceState {
  const journals = { ...state.journals };
  delete journals[journalId];
  return { ...state, journals };
}

/**
 * Look a batch up, run `mutate` on it, and stamp `updatedAt` from the command.
 *
 * The batch-shaped twin of {@link withRecipe}, and for the same reason: every
 * batch-scoped command goes through here, so `updatedAt` is uniform and every
 * inverse can restore it by carrying the pre-state's value as its envelope
 * timestamp.
 */
export function withBatch(
  state: WorkspaceState,
  command: Command,
  batchId: string,
  mutate: (batch: BatchDoc) => BatchDoc | CommandError,
): ApplyResult {
  const batch = state.batches[batchId];
  if (batch === undefined) {
    return {
      ok: false,
      error: commandError('not-found', `No batch with id "${batchId}".`, {
        commandId: command.commandId,
        commandType: command.type,
      }),
    };
  }
  const result = mutate(batch);
  if (isCommandError(result)) {
    return {
      ok: false,
      error: { ...result, commandId: command.commandId, commandType: command.type },
    };
  }
  return { ok: true, state: putBatch(state, { ...result, updatedAt: command.timestamp }) };
}

/**
 * Look a journal up, run `mutate` on it, and stamp `updatedAt` from the command.
 *
 * The journal-shaped twin of {@link withBatch}. Every `journal.*` command that
 * edits an existing document goes through here, which is what makes "every
 * block command bumps `updatedAt`" a property of one function rather than a
 * rule eight definitions have to remember.
 */
export function withJournal(
  state: WorkspaceState,
  command: Command,
  journalId: string,
  mutate: (journal: JournalDoc) => JournalDoc | CommandError,
): ApplyResult {
  const journal = state.journals[journalId];
  if (journal === undefined) {
    return {
      ok: false,
      error: commandError('not-found', `No journal with id "${journalId}".`, {
        commandId: command.commandId,
        commandType: command.type,
      }),
    };
  }
  const result = mutate(journal);
  if (isCommandError(result)) {
    return {
      ok: false,
      error: { ...result, commandId: command.commandId, commandType: command.type },
    };
  }
  return { ok: true, state: putJournal(state, { ...result, updatedAt: command.timestamp }) };
}

/**
 * Look a blending session up, run `mutate` on it, and stamp `updatedAt` from
 * the command.
 *
 * The blend-shaped twin of {@link withBatch} and {@link withJournal}
 * (PLAN-UX-PASS E1). It arrived with the blend's cellar tasks, which are the
 * first `blend.*` commands to edit one list on an existing session: the five
 * before them replace a whole document or one scalar and each spells the
 * lookup out. Those are left as they are — `blend.update` refuses a loop and
 * `blend.setStage` narrows a date, so neither is the plain "find it and change
 * it" this is — and anything list-shaped that comes next goes through here.
 */
export function withBlend(
  state: WorkspaceState,
  command: Command,
  blendId: string,
  mutate: (blend: BlendDoc) => BlendDoc | CommandError,
): ApplyResult {
  const blend = state.blends[blendId];
  if (blend === undefined) {
    return {
      ok: false,
      error: commandError('not-found', `No blend with id "${blendId}".`, {
        commandId: command.commandId,
        commandType: command.type,
      }),
    };
  }
  const result = mutate(blend);
  if (isCommandError(result)) {
    return {
      ok: false,
      error: { ...result, commandId: command.commandId, commandType: command.type },
    };
  }
  return { ok: true, state: putBlend(state, { ...result, updatedAt: command.timestamp }) };
}

/** `not-found` for a missing list item, phrased for the UI. */
export function itemNotFound(list: string, id: string): CommandError {
  return commandError('not-found', `No ${list} with id "${id}" in this recipe.`);
}
